import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { logout } from './AuthSlice'
const apiURL = process.env.REACT_APP_BACKEND

// [86bbwu3px] Live-feed poll cadence — how often the admin dashboard pulls new crossings AND the
// on-site occupancy counts (App.js reschedules getEvents on this; TransactionMonitor polls getGates
// on the same cadence). This is a CONSTANT, not persisted per-user state: it was formerly a field on
// the event slice, which meant a user's stale redux-persist store pinned the OLD interval forever
// after a deploy. As a module constant it takes effect for everyone on the next load, no forced
// logout. Measured cost per poll (local, warm): ~13ms events delta + ~6ms onsite COUNT, both indexed
// and bounded (GateOnsite rows are deleted on OUT) — so 15s is ~9x faster than the old 3min with
// negligible DB/HTTP load. Faster also REDUCES rapid-fire pressure (smaller id-delta batches per
// poll, less chance of hitting the 15-row page cap). True realtime (sub-second) needs server push
// (SSE/WebSocket) — a separate effort. To tune: change this one value (10s snappier ⟷ 30s calmer).
export const EVENT_POLL_MS = 15 * 1000

const initialState = {
  events: [],
  foregroundRequestId: null,
  pollRequestId: null,
  eventCount: 0,
  eventFilters: [],
  eventSorting: [],
  eventPagination: {pageIndex: 0, pageSize: 15},
  eventListReload: 0,
  eventListLoading: false,
  eventListLoadingMore: false,   // bottom "loading more…" spinner (infinite scroll)
  eventHasMore: false,           // are there more pages to lazy-load on scroll?
  eventPage: 1,                  // highest page appended so far (infinite scroll)
  eventListLoaded: 0,
  latestEventTimestamp: '',
  // [86bbwu3px B-ROOT-2] the incremental poll watermarks by the max auto-increment id seen, NOT the
  // event timestamp: a delayed/backfilled crossing has an OLDER timestamp but a HIGHER id, so an
  // id-delta surfaces it whereas the old `timestamp > lastTs` poll dropped it forever.
  latestEventId: 0,
  eventDownloadStatus: false,
  eventDownloadData: false,
  eventDownloadError: '',
  // [86bbwu3px] Live-poll health, driven by the useLivePoll loop (via getEvents). 'ok' while polls
  // succeed; flips to 'error' after POLL_FAIL_THRESHOLD consecutive failures so the feed toolbar can
  // show a subtle "live updates interrupted — Refresh" indicator instead of silently freezing. A
  // single successful poll clears it. NOT persisted meaningfully — re-derived live on each poll.
  pollHealth: 'ok',
  pollFailures: 0,
  // [86bbwu3px item 5] which monitor view is active ('events' | 'log'). Persisted (redux-persist)
  // so a hard refresh restores the view the admin was on instead of snapping back to Gate Events.
  monitorView: 'events',
}

export const getEvents = createAsyncThunk('event/getEvents', async (props, thunkAPI) => {
  // access the entire redux state
  const state = thunkAPI.getState()
  const { authId, subscriberId, customerId, webToken } = state.auth
  const { eventFilters, eventSorting, eventPagination, latestEventId } = state.event
  let { poll, filters, download } = props || {}

  // NOTE: the poll timer is owned by useLivePoll (component-lifecycle), not cleared here anymore.
  // we poll so that user sees the new data, however, if we are NOT on the default state (unfiltered, unsorted, unpaginated)
  // then the result may not be displayed in the current page, in this case the api call is of no use - so abort
  if (poll && (eventFilters.length !== 0 || eventSorting.length !== 0 || eventPagination.pageIndex > 0 || state.event.foregroundRequestId)) {
    return { pollingInvalid: true }
  }

  // Refresh and monitor re-entry must use the same query as the visible filter chips.
  let reqBody = download ? {} : Object.fromEntries(eventFilters.map(filter => [filter.id, filter.value]))
  if (reqBody.timestamp) {
    reqBody.sDate = reqBody.timestamp.sDate
    reqBody.eDate = reqBody.timestamp.eDate
    delete reqBody.timestamp
  }
  if (!download && eventSorting[0]) {
    reqBody.orderBy = eventSorting[0].id
    reqBody.orderDir = eventSorting[0].desc ? 'desc' : 'asc'
  }
  reqBody = { ...reqBody, ...filters }
  // Super (100) is unscoped server-side, so honor the "All subscribers" dropdown it carries in the
  // filters (undefined = every tenant; a value = that one tenant). Every other role is pinned to its
  // OWN subscriber here — the backend re-enforces this in deriveScope regardless, so this is just the
  // client staying honest. Mirrors the customerId rule below (which super/account-admin drive by UI).
  reqBody.subscriberId = (authId === 100) ? reqBody.subscriberId : (parseInt(subscriberId) ? subscriberId : null)
  reqBody.customerId = (!authId || ![100, 1].includes(authId)) ? customerId : reqBody.customerId
  // [86bbwu3px B-ROOT-2] poll by id delta, not timestamp — surfaces late/backfilled crossings.
  reqBody.lastId = poll ? latestEventId : null

  return await fetch( apiURL + 'api/gateeventsNew', {
    method: 'post',
    headers: {
      'Authorization': webToken,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(reqBody),
    credentials: 'include'
  })
  .then( function(response) {
    if (!response.ok) {
        throw Error(response.statusText);
    }
    return response;
  })
  .then(response => response.json() )
  .then(async data => {
    if (!data || !Array.isArray(data.result) || !Number.isFinite(data.count) ||
        data.result.some(row => !row || !Number.isFinite(Number(row.id)) || Number(row.id) <= 0 || typeof row.timestamp !== 'string')) {
      throw Error('Invalid gate events response')
    }
    return data
  })
  .catch(error => {
    console.error('Error:', error)
    // signal the poll-health tracker (reducer) that this fetch failed — drives the "live updates
    // interrupted" indicator. Still resolve with an empty result so no caller crashes.
    return {result: [], count: 0, pollError: true}
  })
})

// A view change invalidates responses issued for the previous query.
const invalidateRequests = state => {
  state.foregroundRequestId = null
  state.pollRequestId = null
  state.eventListLoading = false
  state.eventListLoadingMore = false
}

const eventSlice = createSlice({
  name: 'event',
  initialState,
  reducers: {
    setEventState: (state, action) => {
      if (['eventFilters', 'eventSorting', 'eventPagination'].includes(action.payload.field)) {
        invalidateRequests(state)
      }
      state[action.payload.field] = action.payload.value
    },
    setEventList: (state, action) => {
      state.events = action.payload.events
      state.eventCount = action.payload.eventCount
    },
    setEventFilters: (state, action) => {
      invalidateRequests(state)
      const newFilter = typeof action.payload === 'function' ? action.payload()[0] : action.payload
      let filters = state.eventFilters.filter(f => f.id !== newFilter.id)
      if (newFilter.value) {
        filters.push(newFilter)
      }
      state.eventFilters = filters
    },
    setEventSorting: (state, action) => {
      invalidateRequests(state)
      state.eventSorting = action.payload()
    },
    setEventPagination: (state, action) => {
      invalidateRequests(state)
      state.eventPagination = typeof action.payload === 'function' ? action.payload() : action.payload
    },
    resetEventState: (state, action) => {
      invalidateRequests(state)
      if (action.payload) {
        state.eventFilters = initialState.eventFilters
        state.eventSorting = initialState.eventSorting
        state.eventPagination = initialState.eventPagination
      }
      state.latestEventId = 0
      state.latestEventTimestamp = ''
      state.eventListLoaded = 0
      state.events = initialState.events
      state.eventCount = initialState.eventCount
      state.eventPage = initialState.eventPage
      state.eventHasMore = initialState.eventHasMore
      state.eventListLoadingMore = false
      state.eventListReload = state.eventListReload + 1
      state.eventListLoading = true
      // poll health is derived, not durable — clear any stale persisted 'error' on mount
      // (getInitialData calls resetEventState on every load) so a recovered backend doesn't show a
      // false "live updates interrupted" chip before the first poll runs.
      state.pollHealth = 'ok'
      state.pollFailures = 0
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEvents.pending, (state, action) => {
        if (action.meta.arg?.download) return
        if (action.meta.arg?.poll) {
          state.pollRequestId = action.meta.requestId
        } else {
          state.pollRequestId = null
          state.foregroundRequestId = action.meta.requestId
          state.eventListLoading = !action.meta.arg?.append
          state.eventListLoadingMore = !!action.meta.arg?.append
        }
      })
      .addCase(getEvents.rejected, (state, action) => {
        if (action.meta.arg?.download) return
        const key = action.meta.arg?.poll ? 'pollRequestId' : 'foregroundRequestId'
        if (state[key] !== action.meta.requestId) return
        state[key] = null
        if (!action.meta.arg?.poll) {
          state.eventListLoading = false
          state.eventListLoadingMore = false
        }
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        if (!action.meta.arg?.download) {
          const key = action.meta.arg?.poll ? 'pollRequestId' : 'foregroundRequestId'
          if (state[key] !== action.meta.requestId) return
          state[key] = null
        }
        // ---- poll health (drives the "live updates interrupted" indicator). ONLY a real
        // background POLL failure raises the alarm — a failed manual Refresh or CSV export must not
        // (that's a different action, not the live feed dying). A CSV download never touches health.
        // ANY successful real fetch (poll, refresh, filter, initial load) proves the server is
        // reachable and clears it. Two consecutive poll failures trip it; one success self-heals. ----
        if (!action.payload.pollingInvalid && !action.meta.arg?.download) {
          if (action.payload.pollError && !action.meta.arg?.download) {
            if (action.meta.arg?.poll) {
              state.pollFailures += 1
              if (state.pollFailures >= 2) state.pollHealth = 'error'
            }
          } else {
            state.pollFailures = 0
            state.pollHealth = 'ok'
          }
        }
        if (action.payload.pollError && !action.meta.arg?.download) {
          if (!action.meta.arg?.poll) {
            // A completed initial attempt enables the live loop to retry after an outage.
            state.eventListLoaded += 1
            state.eventListLoading = false
            state.eventListLoadingMore = false
          }
          return
        }
        if (action.meta.arg?.download) {
          // if we are downloading, just return the results directly
          state.eventDownloadData = action.payload.result

        } else if (action.payload.pollingInvalid) {
          // poll skipped (filtered/sorted/paged) — nothing to do; the useLivePoll loop reschedules
          // itself, so no re-arm trigger is needed here.

        } else {
          const rows = action.payload.result || []
          const count = action.payload.count || 0

          // lastId=0 requests an ordinary first page, including the authoritative total.
          // This also initializes pagination when the first fetch failed and a poll recovers.
          if (action.meta.arg.poll && state.latestEventId > 0) {
            // live poll: prepend genuinely-new events (dedupe by id so a boundary row can't double).
            const seen = new Set(state.events.map(e => e.id))
            const fresh = rows.filter(e => !seen.has(e.id))
            state.events = [...fresh, ...state.events].sort((a, b) =>
              b.timestamp.localeCompare(a.timestamp) || Number(b.id) - Number(a.id))
            // grow the running total by the number of rows we ACTUALLY added, NOT the backend's
            // `count`. On a lastId poll `count` is the uncapped delta-since-lastId, but a page only
            // returns ≤15 rows — so `count + eventCount` overstates the "of N" total whenever a burst
            // exceeds the page cap, and it never resyncs. `fresh.length` keeps N consistent with the
            // feed; a view re-entry / filter / Reset does an authoritative REPLACE that re-syncs N.
            state.eventCount = state.eventCount + fresh.length
            // [B-ROOT-2] advance the id watermark by the MAX id in the delta (a delayed upload has a
            // high id but old timestamp), so the next poll starts past it and it isn't re-fetched.
            state.latestEventId = rows.reduce((m, e) => Math.max(m, e.id || 0), state.latestEventId)
            if (state.events[0]?.timestamp > state.latestEventTimestamp) {
              state.latestEventTimestamp = state.events[0].timestamp
            }
          } else if (action.meta.arg.append) {
            // infinite scroll: append the next page, dedupe against what's already loaded
            // (a poll may have prepended rows and shifted the page boundary — dedupe keeps
            // it correct; worst case a load-more advances by <pageSize, never skips/dupes).
            const seen = new Set(state.events.map(e => e.id))
            const fresh = rows.filter(e => !seen.has(e.id))
            state.events = [...state.events, ...fresh]
            state.eventCount = count
            state.eventPage = (action.meta.arg.filters && action.meta.arg.filters.page) || (state.eventPage + 1)
            state.eventHasMore = state.events.length < count
          } else {
            // fresh load / filter or sort change: replace from page 1
            state.events = rows
            state.eventCount = count
            state.eventPage = 1
            state.eventHasMore = rows.length < count
            // [B-ROOT-2] seed the id watermark from the fresh page's MAX id (found by scan, not rows[0]
            // — a delayed upload with a high id may sit lower in the timestamp-ordered page).
            state.latestEventId = rows.reduce((m, e) => Math.max(m, e.id || 0), 0)
            if (rows[0]?.timestamp > state.latestEventTimestamp) {
              state.latestEventTimestamp = rows[0].timestamp
            }
          }
          state.eventListLoading = false
          state.eventListLoadingMore = false
          state.eventListLoaded += 1
        }
      })
      .addCase(logout, (state, action) => {
        return { ...initialState }
      })
  },
})

export const {
  setEventState,
  setEventList,
  setEventFilters,
  setEventSorting,
  setEventPagination,
  resetEventState,
} = eventSlice.actions
export default eventSlice.reducer
